import type { Ref } from 'react'
import { forwardRef } from 'react'

import { css } from 'twind/css'

import { Spinner } from '@core/assets/Spinner'
import { useTwindContext } from '@core/twind'

import type { BaseButtonProps } from './Button.twind'
import {
  borderRadiusSettings,
  bounceAnimationSetting,
  button,
  heightSettings,
  outlinedButtonStyle,
  primaryButtonStyle,
  rippleAnimationSetting,
  rippleRings,
  smallButtonStyle,
  unstyledButton,
} from './Button.twind'

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  BaseButtonProps

// Require href for AnchorButton
export type AnchorButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement> &
  BaseButtonProps & {
    href: string
  }

interface USButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  ref?: Ref<HTMLButtonElement>
  overrideCSS?: ReturnType<typeof css>
}

/**
 *
 */
export const ButtonContent: React.FC<
  React.PropsWithChildren<BaseButtonProps>
> = (props) => {
  const { tw } = useTwindContext()

  const size = props.size ?? 'default'

  return props.isLoading ? (
    <Spinner width={heightSettings[size] * 0.8} label="loading" />
  ) : (
    <>
      {props.icon}
      {props.children && (
        <span
          className={tw?.(
            'overflow-hidden',
            'text-ellipsis',
            'whitespace-nowrap',
          )}
        >
          {props.children}
        </span>
      )}
    </>
  )
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const { tw } = useTwindContext()

    const buttonStyle = css`
      width: ${props.block ? '100%' : 'auto'};
      border-radius: ${borderRadiusSettings[props.borderStyle || 'default']}px;

      ${
        (props.disabled || props.isLoading) &&
        css`
          pointer-events: none;
          cursor: default !important;
        `
      }

      ${
        props.variant === 'outlined' ? outlinedButtonStyle : primaryButtonStyle
      };

      ${props.size === 'small' && smallButtonStyle};

      ${
        props.disabled &&
        css`
          filter: brightness(70%) saturate(50%);

          &:hover,
          &:focus-visible {
            filter: brightness(70%) saturate(50%);
          }
        `
      }

      &:hover,
      &:focus-visible {
        opacity: 0.9;

        // Use important to override global style on anchor tag.
        text-decoration: none !important;
      }

      &:active {
        ${!props.disabled && bounceAnimationSetting};
      }

      &::after {
        ${props.ripple && rippleRings};
        ${props.ripple && rippleAnimationSetting};
        content: '';
      }
    `

    return (
      <button
        className={
          props.className
            ? `${tw?.(button, buttonStyle)} ${props.className}`
            : tw?.(button, buttonStyle)
        }
        onClick={
          !props.disabled && !props.isLoading ? props.onClick : () => null
        }
        disabled={props.disabled || props.isLoading}
        aria-busy={!!props.isLoading}
        ref={ref}
        data-testid={props['data-testid']}
        aria-label={props['aria-label']}
      >
        <ButtonContent {...props} />
      </button>
    )
  },
)

/**
 * An `<a>` tag based button that shares the same props and style as the button.
 * Use this button when you need to navigate to a different page.
 */
export const AnchorButton = forwardRef<HTMLAnchorElement, AnchorButtonProps>(
  (props, ref) => {
    const { tw } = useTwindContext()

    const buttonStyle = css`
      width: ${props.block ? '100%' : 'auto'};
      border-radius: ${borderRadiusSettings[props.borderStyle || 'default']}px;

      ${
        (props.disabled || props.isLoading) &&
        css`
          pointer-events: none;
          cursor: default !important;
        `
      }
      ${
        props.variant === 'outlined' ? outlinedButtonStyle : primaryButtonStyle
      };

      ${props.size === 'small' && smallButtonStyle};

      ${
        props.disabled &&
        css`
          filter: brightness(70%) saturate(50%);

          &:hover,
          &:focus-visible {
            filter: brightness(70%) saturate(50%);
          }
        `
      }

      &:hover,
      &:focus-visible {
        opacity: 0.9;

        // Use important to override global style on anchor tag.
        text-decoration: none !important;
      }

      &:active {
        ${!props.disabled && bounceAnimationSetting};
      }

      &::after {
        ${props.ripple && rippleRings};
        ${props.ripple && rippleAnimationSetting};
        content: '';
      }
    `

    return (
      <a
        className={
          props.className
            ? `${tw?.(button, buttonStyle)} ${props.className}`
            : tw?.(button, buttonStyle)
        }
        onClick={
          !props.disabled && !props.isLoading ? props.onClick : () => null
        }
        role="button"
        aria-busy={!!props.isLoading}
        ref={ref}
        href={props.href}
        data-testid={props['data-testid']}
        target={props.target}
      >
        <ButtonContent {...props} />
      </a>
    )
  },
)

/**
 * An unstyled button
 */
export const UnstyledButton = forwardRef<HTMLButtonElement, USButtonProps>(
  ({ children, className, overrideCSS, ...rest }, ref) => {
    const { tw } = useTwindContext()

    const unstyledButtonStyle = tw?.(
      unstyledButton,
      'border-none',
      'inline-block',
      'p-0',
      'm-0',
      className,
      overrideCSS &&
        css`
          && {
            ${overrideCSS}
          }
        `,
    )

    return (
      <button className={unstyledButtonStyle} {...rest} ref={ref}>
        {children}
      </button>
    )
  },
)
